import { ElementRef, Inject, Injectable } from '@angular/core';
import Env from '../../env';
import { Observable } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { EcommBankcardTransaction, EcommTransactionOptions, TransactionMessage } from '../models/ecomm-form.interface';

@Injectable({
    providedIn: 'root'
})
export class EcommFormService {
    constructor(@Inject('ENV') private env: Env) {}

    /**
     * Subscribes to theme / high-contrast changes, reloads the iframe if the theme toggles,
     * and streams the current isDarkMode state.
     */
    watchThemeChanges(
        highContrast$: Observable<boolean>,
        destroy$: Observable<void>,
        iframe: () => ElementRef<HTMLIFrameElement>,
        isIframeDisplayed: () => boolean
    ): Observable<boolean> {
        let previousDarkMode = false;
        return highContrast$.pipe(
            takeUntil(destroy$),
            tap((isDarkMode) => {
                if (previousDarkMode !== isDarkMode && isIframeDisplayed() && this.env.USE_ECOMM_V2) {
                    this.reloadIframe(iframe());
                }
                previousDarkMode = isDarkMode;
            })
        );
    }

    reloadIframe(iframe: ElementRef<HTMLIFrameElement>): void {
        const iframeElement = iframe?.nativeElement;
        const endpoint = this.env.TPS_BANKCARD_IFRAME_URL;
        if (!iframeElement || !endpoint) {
            return;
        }
        iframeElement.src = 'about:blank';
        iframeElement.src = endpoint;
    }

    getTransactionMessage(
        transaction: EcommBankcardTransaction,
        isDarkMode: boolean,
        options: EcommTransactionOptions
    ): TransactionMessage {
        if (!this.env.USE_ECOMM_V2) {
            return transaction;
        }
        return {
            form: 'pan-token-generation',
            lang: transaction?.lang,
            stylesheetName: this.getStylesheetName(isDarkMode),
            panFormatting: 'enabled',
            transactionId: transaction?.transactionId,
            companyId: transaction?.companyId != null ? String(transaction.companyId) : '',
            additionalCompanyIds: [],
            useShortCardTokenForm: options.useShortCardTokenForm,
            use3DSCardTokenForm: options.use3DSCardTokenForm,
            useExpressCheckout: options.useExpressCheckout ?? false,
            placeholders: options.placeholders ?? true,
            permissiveSubmitButton: options.permissiveSubmitButton,
            datetime: transaction?.datetime,
            appleButtonstyle: 'black',
            googleButtonstyle: 'black',
            hmacFields: transaction?.hmacFields,
            hmac: transaction?.hmac
        };
    }
    private getStylesheetName(isDarkMode: boolean): string {
        const settings = this.env.ECOMM_FORM_SETTINGS;
        return isDarkMode ? settings?.HIGH_CONTRAST_STYLE_NAME || 'default' : settings?.DEFAULT_STYLE_NAME || 'default';
    }
}
